import { useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/_core/hooks/useAuth";
import { useCompany } from "@/contexts/CompanyContext";
import { getLoginUrl } from "@/const";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Archive,
  ArchiveRestore,
  Play,
  Search,
  TrendingUp,
  Calendar,
  DollarSign,
} from "lucide-react";

type TemplateFormData = {
  name: string;
  description: string;
  category: string;
  transactionType: "debit" | "credit" | "transfer";
  defaultDescription: string;
  defaultAmount: string;
  defaultCategory: string;
  defaultAccountCode: string;
  defaultAccountName: string;
  defaultVatRate: string;
  defaultReference: string;
  defaultNotes: string;
  isRecurring: boolean;
  recurrenceFrequency?: "daily" | "weekly" | "monthly" | "quarterly" | "yearly";
  recurrenceDay?: string;
};

export default function Templates() {
  return (
    <DashboardLayout>
      <TemplatesContent />
    </DashboardLayout>
  );
}

function TemplatesContent() {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { selectedCompanyId } = useCompany();

  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isUseDialogOpen, setIsUseDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const [formData, setFormData] = useState<TemplateFormData>({
    name: "",
    description: "",
    category: "",
    transactionType: "debit",
    defaultDescription: "",
    defaultAmount: "",
    defaultCategory: "",
    defaultAccountCode: "",
    defaultAccountName: "",
    defaultVatRate: "",
    defaultReference: "",
    defaultNotes: "",
    isRecurring: false,
  });

  const [useFormData, setUseFormData] = useState({
    date: new Date().toISOString().split("T")[0],
    description: "",
    amount: "",
    reference: "",
    notes: "",
  });

  // Queries
  const { data: templates = [], refetch: refetchTemplates } =
    trpc.transactionTemplates.list.useQuery(
      { companyId: selectedCompanyId || 1, includeArchived: showArchived },
      { enabled: !!selectedCompanyId }
    );

  // Mutations
  const createMutation = trpc.transactionTemplates.create.useMutation({
    onSuccess: () => {
      toast.success("Template created successfully");
      setIsCreateDialogOpen(false);
      resetForm();
      refetchTemplates();
    },
    onError: error => {
      toast.error(`Failed to create template: ${error.message}`);
    },
  });

  const updateMutation = trpc.transactionTemplates.update.useMutation({
    onSuccess: () => {
      toast.success("Template updated successfully");
      setIsEditDialogOpen(false);
      setSelectedTemplate(null);
      resetForm();
      refetchTemplates();
    },
    onError: error => {
      toast.error(`Failed to update template: ${error.message}`);
    },
  });

  const deleteMutation = trpc.transactionTemplates.delete.useMutation({
    onSuccess: () => {
      toast.success("Template deleted successfully");
      refetchTemplates();
    },
    onError: error => {
      toast.error(`Failed to delete template: ${error.message}`);
    },
  });

  const archiveMutation = trpc.transactionTemplates.archive.useMutation({
    onSuccess: () => {
      toast.success("Template archived successfully");
      refetchTemplates();
    },
    onError: error => {
      toast.error(`Failed to archive template: ${error.message}`);
    },
  });

  const restoreMutation = trpc.transactionTemplates.update.useMutation({
    onSuccess: () => {
      toast.success("Template restored successfully");
      refetchTemplates();
    },
    onError: error => {
      toast.error(`Failed to restore template: ${error.message}`);
    },
  });

  const useTemplateMutation = trpc.transactionTemplates.useTemplate.useMutation(
    {
      onSuccess: () => {
        toast.success("Transaction created from template");
        setIsUseDialogOpen(false);
        setSelectedTemplate(null);
        resetUseForm();
        refetchTemplates();
      },
      onError: error => {
        toast.error(`Failed to create transaction: ${error.message}`);
      },
    }
  );

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "",
      transactionType: "debit",
      defaultDescription: "",
      defaultAmount: "",
      defaultCategory: "",
      defaultAccountCode: "",
      defaultAccountName: "",
      defaultVatRate: "",
      defaultReference: "",
      defaultNotes: "",
      isRecurring: false,
    });
  };

  const resetUseForm = () => {
    setUseFormData({
      date: new Date().toISOString().split("T")[0],
      description: "",
      amount: "",
      reference: "",
      notes: "",
    });
  };

  const handleCreate = () => {
    if (!formData.name || !formData.transactionType) {
      toast.error("Name and transaction type are required");
      return;
    }

    createMutation.mutate({
      companyId: selectedCompanyId || 1,
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category || undefined,
      transactionType: formData.transactionType,
      defaultDescription: formData.defaultDescription || undefined,
      defaultAmount: formData.defaultAmount
        ? parseFloat(formData.defaultAmount) * 100
        : undefined,
      defaultCategory: formData.defaultCategory || undefined,
      defaultAccountCode: formData.defaultAccountCode || undefined,
      defaultAccountName: formData.defaultAccountName || undefined,
      defaultVatRate: formData.defaultVatRate
        ? parseInt(formData.defaultVatRate)
        : undefined,
      defaultReference: formData.defaultReference || undefined,
      defaultNotes: formData.defaultNotes || undefined,
      isRecurring: formData.isRecurring,
      recurrenceFrequency: formData.recurrenceFrequency,
      recurrenceDay: formData.recurrenceDay
        ? parseInt(formData.recurrenceDay)
        : undefined,
    });
  };

  const handleEdit = () => {
    if (!selectedTemplate || !selectedCompanyId) return;

    updateMutation.mutate({
      id: selectedTemplate.id,
      companyId: selectedCompanyId,
      name: formData.name,
      description: formData.description || undefined,
      category: formData.category || undefined,
      transactionType: formData.transactionType,
      defaultDescription: formData.defaultDescription || undefined,
      defaultAmount: formData.defaultAmount
        ? parseFloat(formData.defaultAmount) * 100
        : undefined,
      defaultCategory: formData.defaultCategory || undefined,
      defaultAccountCode: formData.defaultAccountCode || undefined,
      defaultAccountName: formData.defaultAccountName || undefined,
      defaultVatRate: formData.defaultVatRate
        ? parseInt(formData.defaultVatRate)
        : undefined,
      defaultReference: formData.defaultReference || undefined,
      defaultNotes: formData.defaultNotes || undefined,
      isRecurring: formData.isRecurring,
      recurrenceFrequency: formData.recurrenceFrequency,
      recurrenceDay: formData.recurrenceDay
        ? parseInt(formData.recurrenceDay)
        : undefined,
    });
  };

  const handleDelete = (id: number) => {
    if (!selectedCompanyId) return;
    setDeleteTargetId(id);
  };

  const handleArchive = (id: number) => {
    if (!selectedCompanyId) return;
    archiveMutation.mutate({ id, companyId: selectedCompanyId });
  };

  const handleRestore = (id: number) => {
    if (!selectedCompanyId) return;
    restoreMutation.mutate({
      id,
      companyId: selectedCompanyId,
      isActive: true,
    });
  };

  const handleUseTemplate = () => {
    if (!selectedTemplate) return;

    useTemplateMutation.mutate({
      templateId: selectedTemplate.id,
      companyId: selectedCompanyId || 1,
      date: new Date(useFormData.date).getTime(),
      description: useFormData.description || undefined,
      amount: useFormData.amount
        ? parseFloat(useFormData.amount) * 100
        : undefined,
      reference: useFormData.reference || undefined,
      notes: useFormData.notes || undefined,
    });
  };

  const openEditDialog = (template: any) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || "",
      category: template.category || "",
      transactionType: template.transactionType,
      defaultDescription: template.defaultDescription || "",
      defaultAmount: template.defaultAmount
        ? (template.defaultAmount / 100).toFixed(2)
        : "",
      defaultCategory: template.defaultCategory || "",
      defaultAccountCode: template.defaultAccountCode || "",
      defaultAccountName: template.defaultAccountName || "",
      defaultVatRate: template.defaultVatRate
        ? template.defaultVatRate.toString()
        : "",
      defaultReference: template.defaultReference || "",
      defaultNotes: template.defaultNotes || "",
      isRecurring: template.isRecurring === 1,
      recurrenceFrequency: template.recurrenceFrequency || undefined,
      recurrenceDay: template.recurrenceDay
        ? template.recurrenceDay.toString()
        : "",
    });
    setIsEditDialogOpen(true);
  };

  const openUseDialog = (template: any) => {
    setSelectedTemplate(template);
    setUseFormData({
      date: new Date().toISOString().split("T")[0],
      description: template.defaultDescription || "",
      amount: template.defaultAmount
        ? (template.defaultAmount / 100).toFixed(2)
        : "",
      reference: template.defaultReference || "",
      notes: template.defaultNotes || "",
    });
    setIsUseDialogOpen(true);
  };

  // Filter templates
  const filteredTemplates = templates.filter((template: any) => {
    const matchesSearch =
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (template.description &&
        template.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  // Group by category
  const groupedTemplates = filteredTemplates.reduce(
    (acc: any, template: any) => {
      const category = template.category || "Uncategorized";
      if (!acc[category]) acc[category] = [];
      acc[category].push(template);
      return acc;
    },
    {}
  );

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = getLoginUrl();
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Transaction Templates</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage recurring transaction templates
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={showArchived ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? (
              <ArchiveRestore className="h-4 w-4 mr-2" />
            ) : (
              <Archive className="h-4 w-4 mr-2" />
            )}
            {showArchived ? "Hide Archived" : "Show Archived"}
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Template
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Templates by Category */}
      {Object.keys(groupedTemplates).length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              No templates found. Create your first template to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(groupedTemplates).map(
          ([category, categoryTemplates]: [string, any]) => (
            <div key={category} className="space-y-4">
              <h2 className="text-xl font-semibold">{category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryTemplates.map((template: any) => (
                  <Card
                    key={template.id}
                    className={template.isActive === 0 ? "opacity-60" : ""}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">
                            {template.name}
                          </CardTitle>
                          {template.description && (
                            <CardDescription className="mt-1">
                              {template.description}
                            </CardDescription>
                          )}
                        </div>
                        {template.isActive === 0 && (
                          <Badge variant="secondary">Archived</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Template Details */}
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Type:</span>
                          <Badge variant="outline">
                            {template.transactionType}
                          </Badge>
                        </div>
                        {template.defaultAmount && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                              Amount:
                            </span>
                            <span className="font-medium">
                              CHF {(template.defaultAmount / 100).toFixed(2)}
                            </span>
                          </div>
                        )}
                        {template.isRecurring === 1 && (
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">
                              Recurs:
                            </span>
                            <Badge variant="secondary">
                              {template.recurrenceFrequency}
                            </Badge>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Used:</span>
                          <span>{template.usageCount} times</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 pt-2 border-t">
                        {template.isActive === 1 ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => openUseDialog(template)}
                              className="flex-1"
                            >
                              <Play className="h-4 w-4 mr-2" />
                              Use
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(template)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchive(template.id)}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleRestore(template.id)}
                              className="flex-1"
                            >
                              <ArchiveRestore className="h-4 w-4 mr-2" />
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDelete(template.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )
        )
      )}

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Template</DialogTitle>
            <DialogDescription>
              Create a new transaction template for recurring or frequently used
              transactions.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Template Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Monthly Rent"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={e =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                  placeholder="e.g., Monthly Bills"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Optional description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="transactionType">Transaction Type *</Label>
                <Select
                  value={formData.transactionType}
                  onValueChange={(value: any) =>
                    setFormData({ ...formData, transactionType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultAmount">Default Amount (CHF)</Label>
                <Input
                  id="defaultAmount"
                  type="number"
                  step="0.01"
                  value={formData.defaultAmount}
                  onChange={e =>
                    setFormData({ ...formData, defaultAmount: e.target.value })
                  }
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="defaultDescription">Default Description</Label>
              <Input
                id="defaultDescription"
                value={formData.defaultDescription}
                onChange={e =>
                  setFormData({
                    ...formData,
                    defaultDescription: e.target.value,
                  })
                }
                placeholder="Transaction description"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="defaultCategory">Default Category</Label>
                <Input
                  id="defaultCategory"
                  value={formData.defaultCategory}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      defaultCategory: e.target.value,
                    })
                  }
                  placeholder="e.g., expense"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="defaultVatRate">VAT Rate (basis points)</Label>
                <Input
                  id="defaultVatRate"
                  type="number"
                  value={formData.defaultVatRate}
                  onChange={e =>
                    setFormData({ ...formData, defaultVatRate: e.target.value })
                  }
                  placeholder="770 for 7.7%"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={e =>
                  setFormData({ ...formData, isRecurring: e.target.checked })
                }
                className="h-4 w-4"
              />
              <Label htmlFor="isRecurring">Recurring Transaction</Label>
            </div>

            {formData.isRecurring && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recurrenceFrequency">Frequency</Label>
                  <Select
                    value={formData.recurrenceFrequency}
                    onValueChange={(value: any) =>
                      setFormData({ ...formData, recurrenceFrequency: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="recurrenceDay">Day (1-31 or 1-7)</Label>
                  <Input
                    id="recurrenceDay"
                    type="number"
                    value={formData.recurrenceDay}
                    onChange={e =>
                      setFormData({
                        ...formData,
                        recurrenceDay: e.target.value,
                      })
                    }
                    placeholder="1"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Creating..." : "Create Template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog - Similar to Create Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>
              Update template details and settings.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Same form fields as Create Dialog */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Template Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={e =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-category">Category</Label>
                <Input
                  id="edit-category"
                  value={formData.category}
                  onChange={e =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-transactionType">Transaction Type *</Label>
                <Select
                  value={formData.transactionType}
                  onValueChange={(value: any) =>
                    setFormData({ ...formData, transactionType: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit</SelectItem>
                    <SelectItem value="credit">Credit</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-defaultAmount">Default Amount (CHF)</Label>
                <Input
                  id="edit-defaultAmount"
                  type="number"
                  step="0.01"
                  value={formData.defaultAmount}
                  onChange={e =>
                    setFormData({ ...formData, defaultAmount: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleEdit} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Template Dialog */}
      <Dialog open={isUseDialogOpen} onOpenChange={setIsUseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Use Template: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription>
              Create a new transaction from this template. You can override any
              default values.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="use-date">Date</Label>
              <Input
                id="use-date"
                type="date"
                value={useFormData.date}
                onChange={e =>
                  setUseFormData({ ...useFormData, date: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="use-description">Description</Label>
              <Input
                id="use-description"
                value={useFormData.description}
                onChange={e =>
                  setUseFormData({
                    ...useFormData,
                    description: e.target.value,
                  })
                }
                placeholder={selectedTemplate?.defaultDescription || ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="use-amount">Amount (CHF)</Label>
              <Input
                id="use-amount"
                type="number"
                step="0.01"
                value={useFormData.amount}
                onChange={e =>
                  setUseFormData({ ...useFormData, amount: e.target.value })
                }
                placeholder={
                  selectedTemplate?.defaultAmount
                    ? (selectedTemplate.defaultAmount / 100).toFixed(2)
                    : ""
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="use-reference">Reference</Label>
              <Input
                id="use-reference"
                value={useFormData.reference}
                onChange={e =>
                  setUseFormData({ ...useFormData, reference: e.target.value })
                }
                placeholder={selectedTemplate?.defaultReference || ""}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUseDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUseTemplate}
              disabled={useTemplateMutation.isPending}
            >
              {useTemplateMutation.isPending
                ? "Creating..."
                : "Create Transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleteTargetId !== null}
        title="Delete Template"
        description="Are you sure you want to permanently delete this template? This action cannot be undone."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleteTargetId !== null && selectedCompanyId) {
            deleteMutation.mutate({
              id: deleteTargetId,
              companyId: selectedCompanyId,
            });
          }
          setDeleteTargetId(null);
        }}
        onCancel={() => setDeleteTargetId(null)}
      />
    </div>
  );
}
